import React, { useEffect, useState } from "react";
import "./PhotoCarousel.css";

function PhotoCarousel({
  photos = [],
  fallbackInitial = "?",
  alt = "Photo",
  className = "",
  imageClassName = "",
  placeholderClassName = "",
  onPhotoClick
}) {
  const safePhotos = Array.isArray(photos)
    ? photos.filter(Boolean)
    : [];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedPhotos, setFailedPhotos] = useState([]);

  const visiblePhotos = safePhotos.filter((photo) => !failedPhotos.includes(photo));
  const hasPhotos = visiblePhotos.length > 0;

  const normalizedIndex = hasPhotos
    ? Math.min(currentIndex, visiblePhotos.length - 1)
    : 0;

  const currentPhoto = hasPhotos ? visiblePhotos[normalizedIndex] : "";

  useEffect(() => {
    if (currentIndex >= visiblePhotos.length && visiblePhotos.length > 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, visiblePhotos.length]);

  function goPrevious(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!hasPhotos) {
      return;
    }

    setCurrentIndex((previousIndex) => {
      if (previousIndex <= 0) {
        return visiblePhotos.length - 1;
      }

      return previousIndex - 1;
    });
  }

  function goNext(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!hasPhotos) {
      return;
    }

    setCurrentIndex((previousIndex) => {
      if (previousIndex >= visiblePhotos.length - 1) {
        return 0;
      }

      return previousIndex + 1;
    });
  }

  function handleImageError() {
    if (currentPhoto) {
      setFailedPhotos((previousPhotos) => [
        ...previousPhotos,
        currentPhoto
      ]);
    }

    setCurrentIndex(0);
  }

  return (
    <div className={`photo-carousel ${className}`}>
      {hasPhotos ? (
        <>
          <img
            src={currentPhoto}
            alt={alt}
            className={`photo-carousel-image ${imageClassName}`}
            onError={handleImageError}
            onClick={() => onPhotoClick?.(currentPhoto)}
            role={onPhotoClick ? "button" : undefined}
            tabIndex={onPhotoClick ? 0 : undefined}
            onKeyDown={(event) => {
              if (onPhotoClick && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onPhotoClick(currentPhoto);
              }
            }}
          />

          {visiblePhotos.length > 1 && (
            <>
              <button
                type="button"
                className="photo-carousel-button photo-carousel-button-left"
                onClick={goPrevious}
                aria-label="Previous photo"
              >
                ‹
              </button>

              <button
                type="button"
                className="photo-carousel-button photo-carousel-button-right"
                onClick={goNext}
                aria-label="Next photo"
              >
                ›
              </button>

              <div className="photo-carousel-dots">
                {visiblePhotos.map((photo, index) => (
                  <button
                    key={`${photo}-${index}`}
                    type="button"
                    className={
                      index === normalizedIndex
                        ? "photo-carousel-dot active"
                        : "photo-carousel-dot"
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    aria-label={`Go to photo ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className={`photo-carousel-placeholder ${placeholderClassName}`}>
          {fallbackInitial}
        </div>
      )}
    </div>
  );
}

export default PhotoCarousel;
